import { memo } from "react";
import type { WithSlots } from "types";
import { when } from "util/support";
import { PROOF_FILE } from "consts/ecommerce";
import {
    acceptRules,
    IdentityPlaceholderTitle,
    ProofFileInput,
    type FileInputProps
} from "ui/forms/formik-elements/FileInput";
import type { OnboardingUploadDocumentsProps } from "./types";
import { DocumentIdentity } from "./entities";

type DocumentFileInputProps<TFormField extends string> = WithSlots<
    OnboardingUploadDocumentsProps,
    {
        readonly proofFileInput:
            & Pick<FileInputProps<TFormField>, 'name'>
            & Partial<Omit<FileInputProps<TFormField>, 'name'>>;
    }
>;

const DocumentFileInput = <TFormField extends string>({
    values,
    isIncomplete,
    setFieldValue,
    setFieldTouched,
    slotProps
}: DocumentFileInputProps<TFormField>) => {
    const name = slotProps?.proofFileInput!.name as TFormField;

    const getFilable = () => {
        if (slotProps?.proofFileInput?.multiple) {
            return {
                files: values[name]
                    .map(doc => doc.documentFileKey)
                    .filter(Boolean) as Array<File | string>
            };
        }

        return {
            file: values[name][0]?.documentFileKey ?? null
        };
    };

    return (
        <ProofFileInput
            <TFormField>
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
            maxFiles={10}
            maxSize={PROOF_FILE.maxSize}
            {...slotProps?.proofFileInput!}
            {...getFilable()}
            disabled={!isIncomplete}
            setFieldValue={(key, value, shouldValidate) => (
                setFieldValue(
                    key,
                    when(
                        slotProps?.proofFileInput?.multiple,
                        () => value.map((file: string | File) => new DocumentIdentity({
                            documentType: name,
                            documentFileKey: file
                        })),
                        () => [new DocumentIdentity({
                            documentType: name,
                            documentFileKey: value
                        })]
                    ),
                    shouldValidate
                )
            )}
            setFieldTouched={setFieldTouched}
            onRemove={(
                when(
                    isIncomplete && slotProps?.proofFileInput?.multiple,
                    () => (file: string | File) => (
                        setFieldValue(
                            name,
                            values[name]
                                .filter(doc => !Object.is(doc.documentFileKey, file)),
                            true
                        )
                    )
                )
            )}
        />
    );
};

export default memo(DocumentFileInput);
